// SUB-OFFICE 7 — the ALTERNATE VANTAGE (first-person rig). Eye height 1.6m,
// pointer-lock mouse-look with pitch clamps, subtle head-bob (disabled under
// prefers-reduced-motion), FOV 70. The camera still belongs to SceneManager:
// this module only computes the eye transform when the cue stack is empty and
// the view is FP. DOM overlays release the pointer lock; a click resumes it.
//
// Consumers: render/scene.ts (SceneManager.fp), boot.ts (wiring + wantLock),
// input/input.ts (yaw for movement, center-screen raycast).

import type { PerspectiveCamera, Vector3 } from 'three';

const EYE_Y = 1.6;
const PITCH_MIN = -1.25; // looking down
const PITCH_MAX = 1.35; // looking up (the lamps must be seeable)
const LOOK_RATE = 0.0022;

export class FirstPersonView {
  yaw = Math.PI; // faces -z at 0; spawn faces west after boot sets it
  pitch = 0;
  private active = false;
  private want = false; // lock desired this frame (no overlay, movement live)
  private locked = false;
  private bobPhase = 0;
  private bobAmp = 0;
  private strafeRoll = 0;
  private crosshair: HTMLElement;
  private hint: HTMLElement;

  constructor(private canvas: HTMLCanvasElement, private reduced: boolean) {
    this.crosshair = document.createElement('div');
    this.crosshair.id = 'fp-crosshair';
    this.crosshair.style.cssText =
      'position:fixed;left:50%;top:50%;width:14px;height:14px;margin:-7px 0 0 -7px;' +
      'pointer-events:none;z-index:20;display:none;opacity:.75';
    // Bureau equipment: a 1px inspection ring with four tick marks —
    // a gunsight drawn by a forms department
    this.crosshair.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 16 16">' +
      '<circle cx="8" cy="8" r="5.2" fill="none" stroke="#e9e4d8" stroke-width="1"/>' +
      '<path d="M8 0.6v2.8M8 12.6v2.8M0.6 8h2.8M12.6 8h2.8" stroke="#e9e4d8" stroke-width="1.2"/></svg>';
    document.body.appendChild(this.crosshair);

    this.hint = document.createElement('div');
    this.hint.id = 'fp-hint';
    this.hint.style.cssText =
      'position:fixed;left:50%;bottom:86px;transform:translateX(-50%);z-index:20;' +
      'font:600 11px ui-monospace,"Courier New",monospace;letter-spacing:.14em;' +
      'color:#e9e4d8;background:rgba(28,26,23,.72);padding:6px 12px;display:none;pointer-events:none';
    this.hint.textContent = 'CLICK TO RESUME THE AUTHORIZED VANTAGE';
    document.body.appendChild(this.hint);

    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.canvas;
      this.updateDom();
    });
    document.addEventListener('mousemove', (e) => {
      if (!this.locked || !this.active) return;
      this.yaw -= e.movementX * LOOK_RATE;
      this.pitch -= e.movementY * LOOK_RATE;
      if (this.pitch < PITCH_MIN) this.pitch = PITCH_MIN;
      if (this.pitch > PITCH_MAX) this.pitch = PITCH_MAX;
    });
    this.canvas.addEventListener('pointerdown', () => {
      if (this.active && this.want && !this.locked) {
        // requestPointerLock returns a Promise in modern browsers; swallow its
        // rejection. Real users clicking the page lock fine, but CDP/DevTools
        // automation rejects with WrongDocumentError — an unhandled rejection
        // there was repeatedly misread by QA as a real-user blocker. It is not.
        const p = this.canvas.requestPointerLock() as unknown as Promise<void> | undefined;
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    });
  }

  get isActive(): boolean {
    return this.active;
  }
  get isLocked(): boolean {
    return this.locked;
  }

  setActive(on: boolean): void {
    this.active = on;
    if (!on && this.locked) document.exitPointerLock();
    this.updateDom();
  }

  /** Per-frame from boot: overlays and dead-movement states release the lock. */
  setWantLock(want: boolean): void {
    if (want === this.want) return;
    this.want = want;
    if (!want && this.locked) document.exitPointerLock();
    this.updateDom();
  }

  private updateDom(): void {
    const looking = this.active && this.locked && this.want;
    this.crosshair.style.display = looking ? 'block' : 'none';
    this.hint.style.display = this.active && this.want && !this.locked ? 'block' : 'none';
  }

  /** Bob readouts for camera-space attachments (render/fphands.ts). */
  get bobPhaseValue(): number {
    return this.bobPhase;
  }
  get bobAmpValue(): number {
    return this.bobAmp;
  }

  /** Face the same heading the avatar last walked (called on view entry). */
  syncFromFacing(facing: number): void {
    this.yaw = facing - Math.PI;
    this.pitch = 0;
  }

  /** Position the camera at the eye. Called by SceneManager when FP owns it. */
  apply(camera: PerspectiveCamera, pos: Vector3, vel: Vector3, dt: number): void {
    const speed = Math.hypot(vel.x, vel.z);
    const s01 = Math.min(1, speed / 2.6);
    if (!this.reduced && speed > 0.15) {
      this.bobPhase += dt * (4.6 + s01 * 4.4);
      this.bobAmp = Math.min(1, this.bobAmp + dt * 5);
    } else {
      this.bobAmp = Math.max(0, this.bobAmp - dt * 5);
    }
    const bobY = Math.abs(Math.sin(this.bobPhase)) * 0.035 * this.bobAmp;
    const roll = Math.sin(this.bobPhase) * 0.006 * this.bobAmp;

    // 0.7-degree lean into strafe: bodies lean (zero under reduced motion)
    const strafe = vel.x * Math.cos(this.yaw) - vel.z * Math.sin(this.yaw);
    const rollTarget = this.reduced ? 0 : -strafe * 0.0047;
    this.strafeRoll += (rollTarget - this.strafeRoll) * Math.min(1, dt * 8);

    camera.position.set(pos.x, EYE_Y + bobY, pos.z);
    camera.rotation.order = 'YXZ';
    camera.rotation.set(this.pitch, this.yaw, roll + this.strafeRoll);
  }
}
